import { displayText } from '../../terminology/store';
import { useTerminology } from '../../terminology/TerminologyContext';
/* WebAppJoin.jsx (fixed, with typography + hidden scrollbars) */
import React, { useState, useContext, useEffect, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import ThemeContext from '../ThemeContext';
import { toast } from 'react-toastify';
import {
  getCsrfToken,
  getCases,
  getDepositionsByCase,
  getArbitrationsByCase,
  getTrialsByCase,
  getMediationsByCase,
  getWitnessPrepsByCase,
  getCustomSessionsByCase,

  getDeposition,
  getArbitration,
  getTrial,
  getMediation,
  getWitnessPrep,
  getCustomSession,

  joinSession,
  getSessionStatus,
  getSessionZoomDetails,
} from '../services/api';

const defaultTheme = {
  name: 'default',
  colors: {
    primary: '#f97316',
    secondary: '#6c757d',
    danger: '#dc3545',
    bgPrimary: '#ffffff',
    textWhite: '#ffffff',
    borderColor: '#dee2e6',
  },
  gradients: {
    primary: '#0d6efd',
    secondary: '#6c757d',
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica Neue", Arial, sans-serif',
    titleFontSize: 24, // px
    bodyFontSize: 14,  // px
  },
};

const retryWithBackoff = async (fn, maxRetries = 3, baseDelay = 1000) => {
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      if (error.response?.status === 429 && attempt < maxRetries) {
        const delay = baseDelay * 2 ** (attempt - 1);
        await new Promise((r) => setTimeout(r, delay));
        continue;
      }
      throw error;
    }
  }
};

const ErrorPopup = memo(({ show, onClose, title, message, theme, fontFamily, bodyFontSize }) => {
  useTerminology();
  if (!show) return null;
  return (
    <div
      className="popup-overlay"
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1050,
        fontFamily,
        fontSize: `${bodyFontSize}px`,
      }}
    >
      <div
        className="popup-box"
        style={{
          backgroundColor: theme.colors.bgPrimary,
          padding: 20,
          borderRadius: 8,
          boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
          textAlign: 'center',
          maxWidth: 460,
          width: '90%',
          minHeight: 180,
        }}
      >
        <div>
          <h5
            style={{
              color: theme.colors.textWhite,
              backgroundColor: theme.colors.primary,
              padding: '10px 40px',
              borderRadius: 5,
              display: 'inline-block',
              width: '100%',
              margin: '0 auto 15px',
            }}
          >
            {title}
          </h5>
          <p style={{ margin: '0 0 15px', padding: '8px 0' }}>{message}</p>
        </div>
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <button
            className="btn btn-primary"
            style={{
              backgroundColor: theme.colors.primary,
              color: theme.colors.textWhite,
              border: 'none',
              padding: '8px 24px',
              borderRadius: 5,
            }}
            onClick={onClose}
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
});

const WebAppJoin = ({ onJoinClick, showForms = false }) => {
  useTerminology();
  const { theme = defaultTheme } = useContext(ThemeContext);
  const navigate = useNavigate();

  // Typography from theme
  const fontFamily =
    theme.typography?.fontFamily || defaultTheme.typography.fontFamily;
  const bodyFontSize =
    theme.typography?.bodyFontSize || defaultTheme.typography.bodyFontSize;
  const titleFontSize =
    theme.typography?.titleFontSize || defaultTheme.typography.titleFontSize;

  // Inject global CSS for hiding scrollbars on elements with .no-scrollbar
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .no-scrollbar {
        -ms-overflow-style: none; /* IE and Edge */
        scrollbar-width: none; /* Firefox */
      }
      .no-scrollbar::-webkit-scrollbar {
        display: none; /* Chrome, Safari, Opera */
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const [witnessForm, setWitnessForm] = useState({
    type: 'witness',
    session_id: '',
    session_passcode: '',
    title: '',
    first_name: '',
    last_name: '',
    email: '',
  });
  const [guestForm, setGuestForm] = useState({
    type: 'guest',
    session_id: '',
    title: '',
    first_name: '',
    last_name: '',
    email: '',
  });

  const [sessions, setSessions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [errorPopup, setErrorPopup] = useState({
    show: false,
    title: '',
    message: '',
  });

  const openErrorPopup = (title, message) =>
    setErrorPopup({ show: true, title, message });
  const closeErrorPopup = () =>
    setErrorPopup({ show: false, title: '', message: '' });

  useEffect(() => {
    const fetchAllSessions = async () => {
      const cached = JSON.parse(localStorage.getItem('cachedSessions') || '{}');
      const cacheDuration = 2 * 60 * 1000;
      if (
        cached.timestamp &&
        Date.now() - cached.timestamp < cacheDuration &&
        cached.data
      ) {
        setSessions(cached.data);
        return;
      }

      try {
        setIsLoading(true);
        await retryWithBackoff(getCsrfToken);
        const casesRes = await retryWithBackoff(getCases);
        const cases = casesRes.data?.data || casesRes.data || [];

        const allSessions = [];

        for (const c of cases) {
  const process = async (arr, type, nameKey) => {
    const list = (Array.isArray(arr.data) ? arr.data : arr.data?.data || [])
      .filter((s) => s.session_type === type);

    for (const s of list) {
      const statusRes = await getSessionStatus(s.id).catch(() => ({
        data: { status: 'unknown', can_join: false },
      }));

      const statusData = statusRes.data?.data || statusRes.data || {};

      allSessions.push({
        ...s,
        session_type:
          type.charAt(0).toUpperCase() + type.slice(1).replace('_', ' '),
        normalizedType: type.replace(/_/g, '-'),
        display_name: s[nameKey] || s.name || `Unnamed ${type}`,
        status: statusData.status || 'unknown',
        can_join: statusData.can_join || false,
        session_passcode: s.session_passcode || '',
      });
    }
  };

  const [dep, arb, tri, med, wp, custom] = await Promise.all([
    retryWithBackoff(() => getDepositionsByCase(c.id)).catch(() => ({ data: [] })),
    retryWithBackoff(() => getArbitrationsByCase(c.id)).catch(() => ({ data: [] })),
    retryWithBackoff(() => getTrialsByCase(c.id)).catch(() => ({ data: [] })),
    retryWithBackoff(() => getMediationsByCase(c.id)).catch(() => ({ data: [] })),
    retryWithBackoff(() => getWitnessPrepsByCase(c.id)).catch(() => ({ data: [] })),
    retryWithBackoff(() => getCustomSessionsByCase(c.id)).catch(() => ({ data: [] })),
  ]);

  await Promise.all([
    process(dep, 'deposition', 'witness'),
    process(arb, 'arbitration', 'name'),
    process(tri, 'trial', 'name'),
    process(med, 'mediation', 'name'),
    process(wp, 'witness_prep', 'witness'),
    process(custom, 'custom', 'custom_type'),
  ]);
}

        const joinable = allSessions.filter((s) => s.can_join);
        localStorage.setItem(
          'cachedSessions',
          JSON.stringify({ data: joinable, timestamp: Date.now() })
        );
        setSessions(joinable);
      } catch (e) {
        toast.error(
          displayText('Failed to load sessions: ') +
            (e.response?.data?.message || e.message)
        );
        setSessions([]);
      } finally {
        setIsLoading(false);
      }
    };

    const timer = setTimeout(fetchAllSessions, 300);
    return () => clearTimeout(timer);
  }, [showForms]);

  const handleWitnessChange = (e) => {
    setWitnessForm({ ...witnessForm, [e.target.name]: e.target.value });
    closeErrorPopup();
  };
  const handleGuestChange = (e) => {
    setGuestForm({ ...guestForm, [e.target.name]: e.target.value });
    closeErrorPopup();
  };

  // Normalize different server shapes and ensure we read actual `status`/`session_type`
  const validateSessionStatus = async (sessionId) => {
    const res = await getSessionStatus(sessionId);
    const data = res.data?.data || res.data || {};
    if (data.status !== 'started') {
      const type = data.session_type
        ? data.session_type
            .charAt(0)
            .toUpperCase() +
          data.session_type.slice(1).replace('_', ' ')
        : 'Session';
      throw new Error(`Cannot login, ${type} not started`);
    }
    return true;
  };

  const handleSubmit = async (formData, type) => {
  if (!formData.session_id) {
    openErrorPopup('Error', 'Session ID is required');
    return;
  }

  if (type === 'witness' && !formData.session_passcode) {
    openErrorPopup('Error', 'Passcode is required for witnesses');
    return;
  }

  try {
    setIsLoading(true);

    await validateSessionStatus(formData.session_id);

    const res = await joinSession(formData);
    const payload = res.data || {};

    const join_request =
      payload.join_request ||
      payload.joinRequest ||
      payload.data?.join_request ||
      payload.data?.joinRequest ||
      null;

    const sessionObj = payload.session || payload.data?.session || null;

    let sessionInfo = sessionObj;
    if (!sessionInfo) {
      sessionInfo = {
        session_type: payload.session_type || payload.sessionType || '',
        status:
          payload.status ||
          payload.session_status ||
          payload.sessionStatus ||
          'started',
        id: payload.session_id || payload.sessionId || formData.session_id,
      };
    }

    if (join_request) {
      localStorage.setItem('join_request', JSON.stringify(join_request));
    }

    localStorage.setItem(
      'currentSession',
      JSON.stringify({
        sessionId: String(formData.session_id),
        type: (sessionInfo.session_type || '').replace(/_/g, '-'),
        userRole: type,
        status: sessionInfo.status || 'started',
        nonMember: true,
      })
    );

    const sessionType = (sessionInfo.session_type || '').replace(/_/g, '-');
    const sessionId = String(formData.session_id);

    const sessionApiMap = {
      deposition: getDeposition,
      arbitration: getArbitration,
      trial: getTrial,
      mediation: getMediation,
      'witness-prep': getWitnessPrep,
      custom: getCustomSession,
    };

    const sessionApi = sessionApiMap[sessionType];

    let fullSession = sessionObj;

    if (sessionApi) {
      const detailsRes = await sessionApi(sessionId);
      fullSession = detailsRes.data?.data || detailsRes.data || fullSession;
    }

    const isZoomSession =
      !!fullSession?.schedule_video &&
      String(fullSession?.zoom_tech || '').toLowerCase() === 'zoom';

    toast.success(
      `${displayText("Welcome ")}${join_request?.title ?? ''}${displayText(" ")}${join_request?.first_name ?? ''}${displayText("! You have joined the session.")}`,
      { autoClose: 2000 }
    );

    if (isZoomSession) {
      const zoomRes = await getSessionZoomDetails(sessionId);
      const zoomData = zoomRes.data?.data || {};

      if (!zoomData.zoom_join_url) {
        throw new Error('Zoom join link not available');
      }

      window.location.href = zoomData.zoom_join_url;
      return;
    }

    navigate('/presentation', {
      state: { sessionId },
    });
  } catch (e) {
    const serverMsg =
      e.response?.data?.error || e.response?.data?.message || e.message || 'Failed to join';

    if (
      e.response?.status === 404 ||
      e.response?.status === 401 ||
      serverMsg.includes('not found') ||
      serverMsg.includes('Invalid')
    ) {
      openErrorPopup('Decline', 'No records found');
    } else if (e.message?.includes('not started')) {
      openErrorPopup('Access Denied', e.message);
    } else {
      openErrorPopup('Error', serverMsg);
    }

    toast.error(serverMsg, { autoClose: 5000 });
  } finally {
    setIsLoading(false);
  }
};

  const availableIds = sessions.filter((s) => s.can_join).map((s) => s.id);
  const getSuggestions = (input) =>
    availableIds
      .filter((id) => String(id).includes(input))
      .slice(0, 3);

  return (
    <div
      className={showForms ? 'd-flex justify-content-center align-items-center' : ''}
      style={
        showForms
          ? {
              minHeight: '80vh',
              width: '100%',
              overflow: 'hidden',
              fontFamily,
              fontSize: `${bodyFontSize}px`,
            }
          : { fontFamily, fontSize: `${bodyFontSize}px` }
      }
    >
      {/* Help + UI */}
      <div
        style={{
          position: 'fixed',
          top: 20,
          right: 20,
          zIndex: 1000,
          fontFamily,
          fontSize: `${bodyFontSize}px`,
        }}
        onMouseEnter={() => setShowHelp(true)}
        onMouseLeave={() => setShowHelp(false)}
      >
        <div
          style={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            background: theme.colors.primary,
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '1.2rem',
            fontWeight: 'bold',
            cursor: 'pointer',
          }}
        >
          ?
        </div>
        {showHelp && (
          <div
            style={{
              position: 'absolute',
              top: 50,
              right: 0,
              background: 'rgba(255,255,255,0.97)',
              border: `1px solid ${theme.colors.borderColor}`,
              borderRadius: 8,
              padding: '0.8rem 1rem',
              maxWidth: 260,
            }}
          >
            <strong style={{ color: theme.colors.primary }}>
              Need Help?
            </strong>
            <ul
              style={{
                margin: '0.4rem 0 0 0',
                paddingLeft: '1.2rem',
                lineHeight: 1.4,
              }}
            >
              <li>{displayText("Contact your administrator for the Session ID")}</li>
              <li>
                <em>Witnesses:</em> you’ll need the passcode
              </li>
              <li>
                <em>Guests:</em> view-only access
              </li>
              <li>{displayText(" Session must be ")}<strong>started</strong> to join
              </li>
            </ul>
          </div>
        )}
      </div>

      {!showForms ? (
        <div
          className="card"
          style={{
            borderRadius: 16,
            background: theme.colors.bgPrimary,
            border: `1px solid ${theme.colors.secondary}`,
            minHeight: 300,
            maxHeight: 300,
            width: '100%',
            maxWidth: 400,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily,
            fontSize: `${bodyFontSize}px`,
          }}
        >
          <div className="card-body p-4 d-flex flex-column align-items-center justify-content-center">
            <i
              className="bi bi-person-slash"
              style={{
                fontSize: '4rem',
                color: theme.colors.secondary,
                marginBottom: '1.5rem',
              }}
            />
            <p className="text-center mb-4">{displayText(" If you don't have an account, click here to join a session. ")}</p>
            <button
              onClick={onJoinClick}
              className="btn w-100 text-white"
              style={{
                background: theme.gradients.secondary,
                border: 'none',
                borderRadius: 8,
                padding: '0.75rem 2rem',
                width: 200,
              }}
            >
              Join
            </button>
          </div>
        </div>
      ) : (
        <div
          className="d-flex flex-row justify-content-center align-items-center w-100"
          style={{
            maxWidth: 800,
            margin: '0 auto',
            columnGap: '1.5rem',
            fontFamily,
            fontSize: `${bodyFontSize}px`,
          }}
        >
          {/* Witness form */}
          <div style={{ flex: '0 0 50%' }}>
            <div
              className="card no-scrollbar"
              style={{
                borderRadius: 16,
                background: theme.colors.bgPrimary,
                border: `1px solid ${theme.colors.secondary}`,
                maxHeight: 450,
                overflowY: 'auto',
                WebkitOverflowScrolling: 'touch',
                width: '100%',
                maxWidth: 340,
                margin: '0 auto',
              }}
            >
              <div
                className="card-header"
                style={{
                  background: theme.gradients.primary,
                  color: theme.colors.textWhite,
                  borderRadius: '16px 16px 0 0',
                  padding: 16,
                  textAlign: 'center',
                }}
              >
                <h3
                  style={{
                    margin: 0,
                    fontSize: `${Math.max(titleFontSize - 8, 14)}px`,
                    fontWeight: 600,
                  }}
                >
                  Join as Witness
                </h3>
              </div>
              <div className="card-body p-3">
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleSubmit(witnessForm, 'witness');
                  }}
                >
                  {/* Session ID */}
                  <div className="mb-3">
                    <label className="form-label">{displayText("Session ID")}</label>
                    <input
                      type="text"
                      className="form-control"
                      name="session_id"
                      value={witnessForm.session_id}
                      onChange={handleWitnessChange}
                      placeholder={displayText("Enter Session ID")}
                      required
                      list="witness-suggestions"
                    />
                    {availableIds.length > 0 && (
                      <datalist id="witness-suggestions">
                        {getSuggestions(witnessForm.session_id).map((id) => (
                          <option key={id} value={id} />
                        ))}
                      </datalist>
                    )}
                  </div>
                  <div className="mb-3">
                    <label className="form-label">{displayText("Session Passcode")}</label>
                    <input
                      type="password"
                      className="form-control"
                      name="session_passcode"
                      value={witnessForm.session_passcode}
                      onChange={handleWitnessChange}
                      placeholder="Enter Passcode"
                      required
                      autoComplete="off"
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Title</label>
                    <select
                      className="form-select"
                      name="title"
                      value={witnessForm.title}
                      onChange={handleWitnessChange}
                      required
                    >
                      <option value="">Select Title</option>
                      <option>Mr</option>
                      <option>Miss</option>
                      <option>Mrs</option>
                      <option>Ms</option>
                      <option>Dr</option>
                      <option>Prof</option>
                    </select>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">First Name</label>
                    <input
                      type="text"
                      className="form-control"
                      name="first_name"
                      value={witnessForm.first_name}
                      onChange={handleWitnessChange}
                      placeholder="First name"
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Last Name</label>
                    <input
                      type="text"
                      className="form-control"
                      name="last_name"
                      value={witnessForm.last_name}
                      onChange={handleWitnessChange}
                      placeholder="Last name"
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Email</label>
                    <input
                      type="email"
                      className="form-control"
                      name="email"
                      value={witnessForm.email}
                      onChange={handleWitnessChange}
                      placeholder="your.email@example.com"
                      required
                    />
                  </div>
                  <button
                    type="submit"
                    className="btn w-100 text-white"
                    style={{
                      background: theme.gradients.primary,
                      border: 'none',
                      borderRadius: 8,
                      padding: '0.75rem',
                      fontWeight: 500,
                    }}
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" />
                        Joining...
                      </>
                    ) : (
                      'Join as Witness'
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>

          {/* Guest form */}
          <div style={{ flex: '0 0 50%' }}>
            <div
              className="card no-scrollbar"
              style={{
                borderRadius: 16,
                background: theme.colors.bgPrimary,
                border: `1px solid ${theme.colors.secondary}`,
                maxHeight: 450,
                overflowY: 'auto',
                WebkitOverflowScrolling: 'touch',
                width: '100%',
                maxWidth: 340,
                margin: '0 auto',
              }}
            >
              <div
                className="card-header"
                style={{
                  background: theme.gradients.secondary,
                  color: theme.colors.textWhite,
                  borderRadius: '16px 16px 0 0',
                  padding: 16,
                  textAlign: 'center',
                }}
              >
                <h3
                  style={{
                    margin: 0,
                    fontSize: `${Math.max(titleFontSize - 8, 14)}px`,
                    fontWeight: 600,
                  }}
                >
                  Join as Guest
                </h3>
              </div>
              <div className="card-body p-3">
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleSubmit(guestForm, 'guest');
                  }}
                >
                  <div className="mb-3">
                    <label className="form-label">{displayText("Session ID")}</label>
                    <input
                      type="text"
                      className="form-control"
                      name="session_id"
                      value={guestForm.session_id}
                      onChange={handleGuestChange}
                      placeholder={displayText("Enter Session ID")}
                      required
                      list="guest-suggestions"
                    />
                    {availableIds.length > 0 && (
                      <datalist id="guest-suggestions">
                        {getSuggestions(guestForm.session_id).map((id) => (
                          <option key={id} value={id} />
                        ))}
                      </datalist>
                    )}
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Title</label>
                    <select
                      className="form-select"
                      name="title"
                      value={guestForm.title}
                      onChange={handleGuestChange}
                      required
                    >
                      <option value="">Select Title</option>
                      <option>Mr</option>
                      <option>Miss</option>
                      <option>Mrs</option>
                      <option>Ms</option>
                      <option>Dr</option>
                      <option>Prof</option>
                    </select>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">First Name</label>
                    <input
                      type="text"
                      className="form-control"
                      name="first_name"
                      value={guestForm.first_name}
                      onChange={handleGuestChange}
                      placeholder="First name"
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Last Name</label>
                    <input
                      type="text"
                      className="form-control"
                      name="last_name"
                      value={guestForm.last_name}
                      onChange={handleGuestChange}
                      placeholder="Last name"
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Email</label>
                    <input
                      type="email"
                      className="form-control"
                      name="email"
                      value={guestForm.email}
                      onChange={handleGuestChange}
                      placeholder="your.email@example.com"
                      required
                    />
                  </div>
                  <button
                    type="submit"
                    className="btn w-100 text-white"
                    style={{
                      background: theme.gradients.secondary,
                      border: 'none',
                      borderRadius: 8,
                      padding: '0.75rem',
                      fontWeight: 500,
                    }}
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" />
                        Joining...
                      </>
                    ) : (
                      'Join as Guest'
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      <ErrorPopup
        show={errorPopup.show}
        onClose={closeErrorPopup}
        title={errorPopup.title}
        message={errorPopup.message}
        theme={theme}
        fontFamily={fontFamily}
        bodyFontSize={bodyFontSize}
      />
    </div>
  );
};

export default WebAppJoin;
